/**
 * Represents API response formats relevant to the Inbox.
 */

import { CustomerMembershipStatus } from './membership-enums';

export enum StaffApprovalAction {
  approve = 'approve',
  deny = 'deny',
  hide = 'hide'
}

export enum PotentialDuplicateCustomerStatus {
  pendingAction = 'PEN',
  merged = 'MER',
  dismissed = 'DIS',
}

export interface InboxCounts {
  inbox_total: number;
  pending_total: number;
  linked_account_requests: number;
  unlinked_reservations: number;
  invalid_documents: number;
  documents_pending_staff_approval: number;
  unlinked_membership_customers: number;
  unlinked_entry_pass_customers: number;
  unlinked_voucher_pack_customers: number;
  memberships_pending_staff_approval: number;
  association_join_requests: number;
  potential_duplicate_customers: number;
  custom_inbox_items: number;
}

export interface UnlinkedReservationPartSubItem {
  start_local_time: string;
  start_local_date: string;
  end_local_time: string;
  end_local_date: string;
}

export interface UnlinkedReservation {
  id: number;
  event_name: string;
  event_parts: UnlinkedReservationPartSubItem[];
  fee_name: string;
  first_name: string;
  last_name: string;
  birthday: string;
  email: string;
}

export interface InvalidDocumentRelatedCustomerSubItem {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  email?: string;
  telephone?: string;
}

export interface InvalidDocument {
  id: number;
  document_id: string;
  document_completed_at: string;
  document_name: string;
  document_pdf: string;
  related_customers: InvalidDocumentRelatedCustomerSubItem[];
  dismissed_by_id?: number;
  dismissed_by_email?: string;
  dismissed_at?: string;
}

export interface PendingDocumentAccountLinkParticipant {
  id: number;
  first_name: string;
  last_name: string;
}

export interface PendingDocumentAccountLink {
  adult: PendingDocumentAccountLinkParticipant;
  minor: PendingDocumentAccountLinkParticipant;
  relation: string;
  relation_label: string;
}

export interface DocumentPendingStaffApproval {
  id: number;
  document_id: string;
  document_completed_at: string;
  document_name: string;
  document_pdf: string;
  related_customers: InvalidDocumentRelatedCustomerSubItem[];
  pending_account_links?: PendingDocumentAccountLink[];
  dismissed_by_id?: number;
  dismissed_by_email?: string;
  dismissed_at?: string;
  resolved_by_id?: number;
  resolved_by_email?: string;
  resolved_at?: string;
  is_denied_staff_approval: boolean;
}

export interface UnlinkedMembershipCustomerInboxItem {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  purchased_at: string;
  customer_membership_id: number;
  membership_name: string;
  membership_is_group: boolean;
  owner_id: number;
  owner_first_name: string;
  owner_last_name: string;
  owner_email: string;
}

export interface UnlinkedEntryPassCustomerInboxItem {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  purchased_at: string;
  entry_pass_name: string;
  owner_id: number;
  owner_first_name: string;
  owner_last_name: string;
  owner_email: string;
}

export interface UnlinkedVoucherPackCustomerInboxItem {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  purchased_at: string;
  voucher_pack_name: string;
  purchaser_id: number;
  purchaser_first_name: string;
  purchaser_last_name: string;
  purchaser_email: string;
}

export interface MembershipPendingStaffApprovalInboxItem {
  id: number;
  purchased_at: string;
  membership_name: string;
  membership_is_group: boolean;
  status: CustomerMembershipStatus;
  owner_id: number;
  owner_first_name: string;
  owner_last_name: string;
  owner_email: string;
}

export interface PotentialDuplicateCustomer {
  id: number;
  url: string;
  new_customer_id: number;
  new_customer_first_name: string;
  new_customer_last_name: string;
  new_customer_birthday: string;
  new_customer_email: string | null;
  new_customer_created_at: string;
  existing_customer_id: number;
  existing_customer_first_name: string;
  existing_customer_last_name: string;
  existing_customer_birthday: string;
  existing_customer_email: string | null;
  existing_customer_created_at: string;
  status: PotentialDuplicateCustomerStatus;
  is_hidden_from_inbox: boolean;
  merged_by_id: number | null;
  merged_by_first_name: string | null;
  merged_by_last_name: string | null;
  merged_by_email: string | null;
  dismissed_by_id: number | null;
  dismissed_by_first_name: string | null;
  dismissed_by_last_name: string | null;
  dismissed_by_email: string | null;
  hidden_by_id: number | null;
  hidden_by_first_name: string | null;
  hidden_by_last_name: string | null;
  hidden_by_email: string | null;
  merged_at: string | null;
  dismissed_at: string | null;
  hidden_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PotentialDuplicateCustomerFilters {
  status?: PotentialDuplicateCustomerStatus;
  is_hidden_from_inbox?: boolean;
  customer_id?: number;
}
